import React from 'react';
import { DelayRange, LOADER_DELAY } from '../constants/timing';

/**
 * Loading BEHAVIOUR — the minimum-visible-duration policy, shared by everything in the app that
 * waits on something. The duration itself lives in `constants/timing`, beside the draft auto-save
 * timings it is repeatedly confused with; that file explains how the two differ.
 *
 * This used to be `pandaLoader.ts`, named after the one component that happened to call it first
 * (components/PandaLoader renders an actual panda). Nothing here is about pandas.
 */

export type { DelayRange } from '../constants/timing';
export { LOADER_DELAY } from '../constants/timing';

export function isTestEnv() {
  return process.env.NODE_ENV === 'test';
}

export function getLoaderDelayMs(range: DelayRange = LOADER_DELAY) {
  // In tests we want deterministic, fast UI transitions.
  if (isTestEnv()) return 0;
  const min = Math.max(0, Number(range.minMs) || 0);
  const max = Math.max(min, Number(range.maxMs) || min);
  return Math.floor(min + Math.random() * (max - min));
}

/**
 * Returns a visibility flag that:
 * - turns on immediately when `isBusy` becomes true
 * - stays on for at least `LOADER_DELAY` (randomized per busy cycle)
 * - turns off once BOTH the work has finished AND that minimum has elapsed
 *
 * ⚠️ Call this unconditionally, like any hook. `busy || useMinimumVisible(busy)` reads naturally
 * but short-circuits the call whenever `busy` is true, changing hook order between renders.
 * Assign the result first, then combine.
 */
export function useMinimumVisible(isBusy: boolean) {
  const [visible, setVisible] = React.useState(false);
  const minDelayRef = React.useRef(0);
  const startRef = React.useRef(0);

  React.useEffect(() => {
    if (isBusy) {
      minDelayRef.current = getLoaderDelayMs();
      startRef.current = Date.now();
      setVisible(true);
      return;
    }

    if (!visible) return;
    const elapsed = Date.now() - (startRef.current || Date.now());
    const remaining = Math.max(0, (minDelayRef.current || 0) - elapsed);
    const t = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(t);
  }, [isBusy, visible]);

  return visible;
}
